import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import {
  AutoAwesome,
  Hub,
  ReceiptLong,
  TrendingUp,
  Link as LinkIcon,
} from '@mui/icons-material';

/**
 * Colores de marca Kobra IA
 * Verde (dinero / acción) · Azul (tech / confianza) · Navy (base SaaS)
 */
const K = {
  green: '#6BD56F',
  blue: '#4185F1',
  navy: '#121826',
};

const C = {
  ...K,
  /** Texto secundario (derivado del navy, no otro matiz) */
  textMuted: alpha(K.navy, 0.62),
  /** Fondos suaves: tintes desde azul / verde de marca */
  mintBg: alpha(K.green, 0.08),
  iceBlue: alpha(K.blue, 0.1),
  pageTint: alpha(K.blue, 0.04),
  /** Fondo secciones: mayoría azul, toque mínimo de verde */
  pageTintMix: `linear-gradient(165deg, ${alpha(K.blue, 0.08)} 0%, ${alpha(K.blue, 0.06)} 50%, ${alpha(K.green, 0.025)} 100%)`,
};

const BR = { card: '24px', cardLg: '28px', pill: 9999, cta: '32px' };

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/images/logotipo_kobraIA_removebg.png`;

function HeroGlobeVisual() {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: BR.cardLg,
        overflow: 'hidden',
        bgcolor: C.navy,
        minHeight: { xs: 280, md: 360 },
        boxShadow: `0 24px 64px ${alpha(K.navy, 0.35)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: '120%',
          height: '120%',
          left: '-10%',
          top: '-10%',
          background: `
            radial-gradient(circle at 30% 40%, ${alpha(K.blue, 0.42)} 0%, transparent 45%),
            radial-gradient(circle at 70% 55%, ${alpha(K.green, 0.18)} 0%, transparent 48%),
            radial-gradient(ellipse at 50% 100%, ${alpha(K.blue, 0.12)} 0%, transparent 55%)
          `,
        }}
      />
      <Box
        sx={{
          position: 'relative',
          width: { xs: 200, md: 260 },
          height: { xs: 200, md: 260 },
          borderRadius: '50%',
          background: `
            radial-gradient(circle at 35% 30%, rgba(255,255,255,0.15) 0%, transparent 50%),
            linear-gradient(145deg, ${alpha(K.blue, 0.88)} 0%, ${alpha(K.navy, 0.94)} 50%, ${alpha(K.blue, 0.35)} 100%)
          `,
          boxShadow: `
            0 0 72px ${alpha(K.blue, 0.4)},
            0 0 40px ${alpha(K.green, 0.18)},
            inset 0 0 60px rgba(255,255,255,0.08)
          `,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '12%',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.12)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '22%',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      />
    </Box>
  );
}

export function Landing() {
  const [logoFailed, setLogoFailed] = React.useState(false);

  return (
    <Box sx={{ bgcolor: '#FFFFFF', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${alpha(K.navy, 0.06)}`,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 2 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              component={RouterLink}
              to="/"
              sx={{ textDecoration: 'none', color: 'inherit' }}
            >
              {!logoFailed ? (
                <Box
                  component="img"
                  src={LOGO_SRC}
                  alt="Kobra IA"
                  onError={() => setLogoFailed(true)}
                  sx={{ height: 45, width: 'auto', objectFit: 'contain' }}
                />
              ) : (
                <Typography sx={{ fontWeight: 800, fontSize: '1.35rem' }}>
                  <Box component="span" sx={{ color: C.blue }}>
                    Kobra
                  </Box>
                  <Box component="span" sx={{ color: alpha(K.navy, 0.75) }}>
                    {' '}
                    IA
                  </Box>
                </Typography>
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={{ xs: 2, md: 4 }}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
             
            </Stack>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              sx={{
                borderRadius: BR.pill,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: C.blue,
                boxShadow: `0 8px 24px ${alpha(K.blue, 0.38)}`,
                '&:hover': { bgcolor: darken(K.blue, 0.08) },
              }}
            >
              Iniciar sesión
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.25rem', md: '3rem' },
                lineHeight: 1.15,
                color: C.navy,
                letterSpacing: '-0.02em',
                mb: 2,
              }}
            >
              Gestión de{' '}
              <Box component="span" sx={{ color: C.navy }}>
                Cobros
              </Box>{' '}
              <Box component="span" sx={{ color: C.blue }}>
                Inteligente
              </Box>
            </Typography>
            <Typography
              sx={{
                color: C.textMuted,
                fontSize: '1.05rem',
                lineHeight: 1.7,
                mb: 4,
                maxWidth: 480,
              }}
            >
              Automatiza seguimiento, comprobantes e{' '}
              <Box component="span" sx={{ color: C.blue, fontWeight: 600 }}>
                integraciones
              </Box>{' '}
              en un solo lugar. Menos fricción, más{' '}
              <Box component="span" sx={{ color: C.navy, fontWeight: 600 }}>
                recuperación
              </Box>{' '}
              y equipos enfocados en lo que importa.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="large"
                sx={{
                  borderRadius: BR.pill,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  bgcolor: C.blue,
                  boxShadow: `0 12px 32px ${alpha(K.blue, 0.38)}`,
                  '&:hover': { bgcolor: darken(K.blue, 0.08) },
                }}
              >
                Prueba gratis
              </Button>
              <Button
                href="#how-it-works"
                variant="outlined"
                size="large"
                sx={{
                  borderRadius: BR.pill,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderColor: C.blue,
                  color: C.blue,
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2, borderColor: C.blue, bgcolor: alpha(K.blue, 0.06) },
                }}
              >
                Ver demo
              </Button>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <HeroGlobeVisual />
          </Grid>
        </Grid>
      </Container>

      {/* Stats */}
      <Box sx={{ background: C.pageTintMix, py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="stretch">
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack alignItems={{ xs: 'center', md: 'flex-start' }} spacing={0.5}>
                <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: C.blue }}>+10%</Typography>
                <Typography sx={{ color: C.textMuted, fontSize: '0.95rem' }}>
                  aumento promedio en cobros
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: BR.cardLg,
                  p: 3,
                  height: '100%',
                  border: `1px solid ${alpha(K.navy, 0.08)}`,
                  boxShadow: `
                    inset 0 4px 0 0 ${K.blue},
                    inset 0 -4px 0 0 ${alpha(K.navy, 0.2)},
                    0 12px 40px ${alpha(K.navy, 0.08)}
                  `,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: C.blue }}>
                  + 10M
                </Typography>
                <Typography sx={{ color: C.textMuted, fontSize: '0.95rem' }}>
                  gestionados satisfactoriamente
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack alignItems={{ xs: 'center', md: 'flex-end' }} spacing={0.5}>
                <Typography sx={{ fontWeight: 800, fontSize: '2rem', color: C.blue }}>24/7</Typography>
                <Typography sx={{ color: C.textMuted, fontSize: '0.95rem', textAlign: { xs: 'center', md: 'right' } }}>
                  automatización activa
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Bento features */}
      <Container id="features" maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography
          variant="h2"
          align="center"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            color: C.navy,
            mb: 1,
            px: 2,
          }}
        >
          Potencia tu{' '}
          <Box component="span" sx={{ color: C.blue }}>
            departamento
          </Box>{' '}
          de{' '}
          <Box component="span" sx={{ color: C.navy }}>
            cobros
          </Box>
        </Typography>
        <Box
          sx={{
            width: 72,
            height: 4,
            borderRadius: 2,
            background: `linear-gradient(90deg, ${K.blue}, ${alpha(K.blue, 0.35)})`,
            mx: 'auto',
            mb: 4,
          }}
        />
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: BR.cardLg,
                p: { xs: 3, md: 4 },
                boxShadow: `0 16px 48px ${alpha(K.navy, 0.07)}`,
                border: `1px solid ${alpha(K.navy, 0.06)}`,
                borderLeft: `5px solid ${K.blue}`,
                height: '100%',
                minHeight: 280,
              }}
            >
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: C.navy, mb: 1.5 }}>
                    Automatización con IA
                  </Typography>
                  <Typography sx={{ color: C.textMuted, fontSize: '0.95rem', lineHeight: 1.65 }}>
                    Bots y reglas que priorizan cuentas, envían recordatorios y aprenden del comportamiento de pago.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 7 }}>
                  <Box
                    sx={{
                      bgcolor: C.navy,
                      borderRadius: 2,
                      p: 2,
                      color: '#fff',
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      lineHeight: 1.6,
                      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                    }}
                  >
                    <Box sx={{ color: C.blue, mb: 0.5 }}>{'>'} pipeline · prioridad alta</Box>
                    <Box sx={{ color: alpha(K.blue, 0.85), fontSize: '0.65rem', mb: 0.5 }}>
                      ● sync ok · salud cartera
                    </Box>
                    <Box sx={{ opacity: 0.85 }}>Cliente ACME · $12,400 · D+18</Box>
                    <Box sx={{ opacity: 0.6, mt: 1 }}>→ recordatorio multicanal programado</Box>
                    <Box sx={{ opacity: 0.45, mt: 1 }}>→ SLA respuesta 4h</Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              id="security"
              sx={{
                bgcolor: C.mintBg,
                borderRadius: BR.cardLg,
                p: 3,
                height: '100%',
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: `0 12px 36px ${alpha(K.blue, 0.12)}`,
              }}
            >
              <ReceiptLong sx={{ fontSize: 40, color: C.blue, mb: 2 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: C.navy, mb: 1 }}>
                Gestión de comprobantes
              </Typography>
              <Typography sx={{ color: C.textMuted, fontSize: '0.95rem', flex: 1, lineHeight: 1.65 }}>
                Conciliación y validación de pagos con trazabilidad completa para tu equipo financiero.
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  alignSelf: 'flex-start',
                  px: 2,
                  py: 0.5,
                  borderRadius: BR.pill,
                  background: `linear-gradient(90deg, ${alpha(K.blue, 0.2)}, ${alpha(K.blue, 0.12)})`,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: C.navy,
                }}
              >
                POWERED SECURITY
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                bgcolor: C.iceBlue,
                borderRadius: BR.cardLg,
                p: 3,
                height: '100%',
                minHeight: 220,
                border: `1px solid ${alpha(K.blue, 0.18)}`,
                boxShadow: `0 8px 28px ${alpha(K.blue, 0.1)}`,
              }}
            >
              <TrendingUp sx={{ fontSize: 36, color: C.blue, mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: C.navy, mb: 1 }}>
                Recuperación de cartera
              </Typography>
              <Typography sx={{ color: C.textMuted, fontSize: '0.92rem', lineHeight: 1.65 }}>
                Segmentación y seguimiento estructurado para reducir mora sin saturar a tus clientes.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Box
              sx={{
                bgcolor: C.navy,
                borderRadius: BR.cardLg,
                p: { xs: 3, md: 4 },
                color: '#fff',
                height: '100%',
                minHeight: 220,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 3,
                boxShadow: `0 20px 50px ${alpha(K.navy, 0.35)}`,
              }}
            >
              <Box sx={{ flex: '1 1 220px' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 1.5 }}>
                  Integración fluida
                </Typography>
                <Typography sx={{ opacity: 0.85, fontSize: '0.95rem', lineHeight: 1.65, mb: 2 }}>
                  Conecta ERP, bancos y canales de mensajería. Un solo flujo para todo el ciclo de cobro.
                </Typography>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  sx={{
                    borderRadius: BR.pill,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: C.blue,
                    boxShadow: `0 8px 24px ${alpha(K.blue, 0.35)}`,
                    '&:hover': { bgcolor: darken(K.blue, 0.08) },
                  }}
                >
                  Ver integraciones
                </Button>
              </Box>
              <Box
                sx={{
                  flex: '1 1 180px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Hub
                  sx={{
                    fontSize: 120,
                    color: K.blue,
                    opacity: 0.55,
                    filter: `drop-shadow(0 0 22px ${alpha(K.blue, 0.5)})`,
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* How it works */}
      <Box id="how-it-works" sx={{ background: C.pageTintMix, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              color: C.navy,
              mb: 1,
            }}
          >
            ¿Cómo funciona{' '}
            <Box component="span" sx={{ color: C.blue }}>
              Kobra
            </Box>
            <Box component="span" sx={{ color: alpha(K.navy, 0.75) }}>
              {' '}
              IA
            </Box>
            ?
          </Typography>
          <Box
            sx={{
              width: 72,
              height: 4,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${K.blue}, ${alpha(K.blue, 0.4)})`,
              mx: 'auto',
              mb: 5,
            }}
          />
          <Grid container spacing={4}>
            {[
              {
                n: 1,
                title: 'Conecta',
                body: 'Vincula tus fuentes de datos y canales en minutos, sin proyectos interminables.',
                icon: <LinkIcon />,
              },
              {
                n: 2,
                title: 'Automatiza',
                body: 'Define estrategias, bots y recordatorios que se adaptan a cada segmento.',
                icon: <AutoAwesome />,
              },
              {
                n: 3,
                title: 'Recupera',
                body: 'Mide resultados en tiempo real y ajusta con datos, no con suposiciones.',
                icon: <TrendingUp />,
              },
            ].map((step) => {
              const accent = K.blue;
              return (
              <Grid key={step.n} size={{ xs: 12, md: 4 }}>
                <Stack alignItems="center" textAlign="center" spacing={2}>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        bgcolor: alpha(accent, 0.14),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: accent,
                        border: `2px solid ${alpha(accent, 0.35)}`,
                        '& svg': { fontSize: 32 },
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        bgcolor: accent,
                        color: '#fff',
                        fontSize: '0.8rem',
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px ${alpha(accent, 0.4)}`,
                      }}
                    >
                      {step.n}
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: C.navy }}>
                    <Box component="span" sx={{ color: accent }}>
                      {step.title.charAt(0)}
                    </Box>
                    {step.title.slice(1)}
                  </Typography>
                  <Typography sx={{ color: C.textMuted, fontSize: '0.95rem', lineHeight: 1.65, maxWidth: 320 }}>
                    {step.body}
                  </Typography>
                </Stack>
              </Grid>
            );
            })}
          </Grid>
        </Container>
      </Box>

      {/* Pricing anchor */}
      <Box id="pricing" sx={{ height: 0, overflow: 'hidden' }} aria-hidden />

      {/* CTA */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box
          sx={{
            background: `linear-gradient(125deg, ${K.blue} 0%, ${darken(K.blue, 0.12)} 55%, ${alpha(K.green, 0.75)} 100%)`,
            borderRadius: BR.cta,
            px: { xs: 3, md: 8 },
            py: { xs: 5, md: 7 },
            textAlign: 'center',
            color: '#fff',
            boxShadow: `0 24px 56px ${alpha(K.blue, 0.35)}, 0 8px 32px ${alpha(K.green, 0.12)}`,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' }, mb: 2 }}>
            Lleva tu cobranza al siguiente nivel
          </Typography>
          <Typography sx={{ opacity: 0.92, fontSize: '1.05rem', maxWidth: 560, mx: 'auto', mb: 4 }}>
            Equipos de cobro y finanzas ya usan Kobra IA para recuperar más, con menos esfuerzo manual.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              size="large"
              sx={{
                borderRadius: BR.pill,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: '#fff',
                color: C.blue,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              }}
            >
              Empezar ahora
            </Button>
            <Button
              href="mailto:ventas@kobraia.com"
              variant="outlined"
              size="large"
              sx={{
                borderRadius: BR.pill,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'rgba(255,255,255,0.85)',
                color: '#fff',
                borderWidth: 2,
                '&:hover': { borderWidth: 2, borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Hablar con ventas
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          borderTop: `1px solid ${alpha(K.navy, 0.08)}`,
          py: 4,
          bgcolor: '#fff',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems={{ xs: 'center', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1} component={RouterLink} to="/" sx={{ textDecoration: 'none' }}>
              {!logoFailed ? (
                <Box
                  component="img"
                  src={LOGO_SRC}
                  alt="Kobra IA"
                  sx={{ height: 28, opacity: 0.9 }}
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  <Box component="span" sx={{ color: C.blue }}>
                    Kobra
                  </Box>
                  <Box component="span" sx={{ color: alpha(K.navy, 0.75) }}>
                    {' '}
                    IA
                  </Box>
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" justifyContent="center">
              <Link
                href="#"
                underline="hover"
                sx={{ color: C.textMuted, fontSize: '0.85rem', '&:hover': { color: C.blue } }}
              >
                Términos del servicio
              </Link>
              <Link
                component={RouterLink}
                to="/privacidad"
                underline="hover"
                sx={{ color: C.textMuted, fontSize: '0.85rem', '&:hover': { color: C.blue } }}
              >
                Política de privacidad
              </Link>
            </Stack>
            <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>
              © {new Date().getFullYear()} Kobra IA. Todos los derechos reservados.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
